import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { TabsBarTab } from '@e3dc-react/shell/Elements/Tabs/TabsBar'
import { ControlledDto } from 'src/Libs/flow/installation/api'
import { ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { parseModbusTcpChargingStation } from 'src/Libs/flow/parsers'
import { useSystemSettingsTitleAndBreadcrumbs } from 'src/Libs/system-settings'
import { getEndUserSettingsWallboxDetailsRoutes } from '../../general/utils/routes'
import { Breadcrumb } from '@e3dc-react/shell/Elements/Header/Breadcrumbs'

type PageParams = {
  pairedEvses: Nullable<ControlledDto>[]
  installationId: string
  storageId?: string
  parentBreadcrumb: Breadcrumb
}

type ReturnType = {
  currentEvse: Nullable<ControlledDto>
  isModbusTcp: boolean
  modbusTcpEvse: ModbusTcpChargingStation | null
}

/**
 * Custom hook to manage ChargingStationPage state and side effects.
 * Handles header tabs, URL-based EVSE selection, and redirects.
 */
export const useChargingStation = ({ pairedEvses, installationId, storageId, parentBreadcrumb }: PageParams): ReturnType => {
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { setTabs, setActiveTab } = useHeaderTabs()

  const title = t('framework.end-user.wallbox')
  const { evseId } = useParams<{ evseId: string }>()

  // Find the currently selected EVSE based on URL param
  const currentEvse = useMemo(() => {
    if (!evseId) return pairedEvses[0] ?? null
    return pairedEvses.find(evse => evse?.id?.toString() === evseId || evse?.index?.toString() === evseId) ?? pairedEvses[0] ?? null
  }, [evseId, pairedEvses])

  const isModbusTcp = currentEvse?.media === 'ModbusTCP'
  const modbusTcpEvse = isModbusTcp && currentEvse ? parseModbusTcpChargingStation(currentEvse) : null

  // Build tabs for each wallbox
  const wallboxTabs: TabsBarTab[] = useMemo(() => {
    return pairedEvses.map(evse => ({
      label: evse?.deviceName || t('installation.evseEditor.title'),
      href: getEndUserSettingsWallboxDetailsRoutes(installationId, storageId, evse?.id?.toString() ?? ''),
      onClick: (href?: string) => {
        if (href) {
          navigate(href)
        }
      },
    }))
  }, [pairedEvses, installationId, storageId, navigate, t])

  // Find the active tab index
  const activeTabIndex = useMemo(() => {
    if (!evseId) return 0
    const index = pairedEvses.findIndex(evse => evse?.id?.toString() === evseId || evse?.index?.toString() === evseId)
    return index > -1 ? index : 0
  }, [evseId, pairedEvses])

  // Set up header tabs
  useEffect(() => {
    dispatch(appUpdate({ title }))
    setTabs(wallboxTabs)
    setActiveTab(activeTabIndex)

    return () => {
      setTabs([])
      setActiveTab(0)
    }
  }, [wallboxTabs, activeTabIndex, title, dispatch, setTabs, setActiveTab])

  useSystemSettingsTitleAndBreadcrumbs({ pageTitleKey: 'framework.end-user.wallbox', breadcrumb: parentBreadcrumb })

  // Redirect to first wallbox if no evseId in URL
  useEffect(() => {
    if (!evseId && pairedEvses.length > 0 && pairedEvses[0]?.id) {
      navigate(getEndUserSettingsWallboxDetailsRoutes(installationId, storageId, pairedEvses[0].id.toString()), { replace: true })
    }
  }, [evseId, pairedEvses, installationId, storageId, navigate])

  return {
    currentEvse,
    isModbusTcp,
    modbusTcpEvse,
  }
}
